<template>
  <div class="flex w-full flex-col gap-4">
    <div class="flex min-h-[min(24rem,calc(80vh-12rem))] flex-1 flex-col gap-3">
      <div class="shrink-0">
        <WizardProgressBar
          v-model:step="localStep"
          :steps="stepsForProgressBar"
          size="extra-small"
        />
      </div>
      <div class="relative flex min-h-0 flex-1 flex-col">
        <div
          ref="stepArea"
          class="relative -my-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-1"
          @scroll.passive="updateHasContentBelow"
        >
          <div ref="stepContent">
            <div v-if="!payment" />
            <div v-else-if="currentStepKey === 'mode'">
              <SelectType
                v-model:payment="localPayment"
                :custom-payment-modes="customPaymentModes"
                :has-dossier="hasDossier"
                :errors="errors"
              />
            </div>
            <div v-else-if="currentStepKey === 'issuer'">
              <SelectIssuer
                v-model:payment="localPayment"
                :dossier-contacts="dossierContacts"
                :place-holder-issuer="placeHolderIssuer"
                :place-holder-direct-debit="placeHolderDirectDebit"
                :errors="errors"
              />
            </div>
            <div v-else-if="currentStepKey === 'amount'">
              <SelectAmount
                v-model:payment="localPayment"
                v-model:cheque-duplicate="localChequeDuplicate"
                :errors="errors"
                :default-bank-account-id="defaultBankAccountId"
                :currency="currency"
              />
            </div>
            <div v-else-if="currentStepKey === 'linking'">
              <slot name="linking" />
            </div>
            <div v-else-if="currentStepKey === 'other'">
              <SelectOtherInformations
                v-model:payment="localPayment"
                :errors="errors"
              >
                <template #other-informations>
                  <slot name="other-informations" />
                </template>
              </SelectOtherInformations>
            </div>
          </div>
        </div>
        <div
          data-test="step-area-fade"
          aria-hidden="true"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-white transition-opacity dark:from-slate-700"
          :class="{ 'opacity-0': !hasContentBelow }"
        />
      </div>
    </div>
    <div class="flex shrink-0 flex-row justify-end gap-2">
      <BaseButton
        v-if="step > 0"
        :title="$t('button.previous')"
        icon="arrow-left"
        color="secondary"
        inverse-icon-order
        @click="localStep--"
      />
      <BaseButton
        v-if="step < stepsForProgressBar.length - 1"
        :title="$t('button.next')"
        color="secondary"
        icon="arrow-right"
        :disabled="!stepValid"
        @click="localStep++"
      />
      <BaseButton
        :title="$t('button.save')"
        :icon="'paper-plane'"
        :display-loader="formSubmitting"
        :disabled="formSubmitting || !allIsValid"
        color="primary"
        data-faro-user-action-name="invoice.add_payment"
        @click="$emit('submit:payment')"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import SelectOtherInformations from '@ui/modal/payment/components/SelectOtherInformations.vue'
import SelectType from '@ui/modal/payment/components/SelectType.vue'
import SelectAmount from '@ui/modal/payment/components/SelectAmount.vue'
import SelectIssuer from '@ui/modal/payment/components/SelectIssuer.vue'
import WizardProgressBar from '@ui/wizard/WizardProgressBar.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapGetters } from 'vuex'
import { isChequeMode } from '@ui/modal/payment/chequeNumberDuplicate'

/** The linking step sits right after the amount, the three before it being fixed. */
const LINKING_STEP_INDEX = 3

export default defineComponent({
  name: 'PaymentModalWizard',
  components: {
    BaseButton,
    WizardProgressBar,
    SelectIssuer,
    SelectAmount,
    SelectType,
    SelectOtherInformations
  },
  props: {
    payment: {
      type: Object,
      required: true
    },
    step: {
      type: Number,
      required: true
    },
    placeHolderIssuer: {
      type: String,
      required: true
    },
    placeHolderDirectDebit: {
      type: String,
      required: true
    },
    dossierContacts: {
      type: Array,
      required: true
    },
    formSubmitting: {
      type: Boolean,
      required: true
    },
    customPaymentModes: {
      type: Array,
      required: true
    },
    hasDossier: {
      type: Boolean,
      required: true
    },
    withOtherSection: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Shows the step carrying the `linking` slot. The owner only turns it on
     * when that slot has something to show: an empty step would be a dead end.
     */
    withLinkingStep: {
      type: Boolean,
      required: false,
      default: false
    },
    funeralHomeId: {
      type: String,
      required: false,
      default: null
    },
    currency: {
      type: String,
      required: true
    },
    /** The cheque already carrying the number typed, which blocks the save. */
    chequeDuplicate: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: [
    'update:payment',
    'update:step',
    'update:cheque-duplicate',
    'submit:payment'
  ],
  data() {
    return {
      /** Part of the step is hidden below the fold: shown as a fade. */
      hasContentBelow: false,
      stepAreaObserver: null
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata',
      payment_modes: 'metadata/getPaymentModesMetadata',
      invoice: 'invoicing/getInvoice'
    }),
    localPayment: {
      get() {
        return this.payment
      },
      set(value) {
        this.$emit('update:payment', value)
      }
    },
    localChequeDuplicate: {
      get() {
        return this.chequeDuplicate
      },
      set(value) {
        this.$emit('update:cheque-duplicate', value)
      }
    },
    localStep: {
      get() {
        return this.step
      },
      set(value) {
        this.$emit('update:step', value)
      }
    },
    currentStepKey() {
      return this.stepsList[this.step]?.key ?? null
    },
    stepsForProgressBar() {
      return this.stepsList.map((step, idx) => {
        return {
          ...step,
          id: idx,
          status: step.valid
            ? 'complete'
            : idx === this.step
              ? 'current'
              : 'upcoming'
        }
      })
    },
    stepsList() {
      let steps = [
        {
          key: 'mode',
          name:
            this.payment?.payment_mode && this.selectedPaymentMode
              ? this.selectedPaymentMode.name
              : this.$t('attributes.payment_mode'),
          valid:
            this.payment?.payment_mode !== null &&
            (this.customPaymentModes.length === 0 ||
              this.payment?.payment_mode_id !== null)
        },
        {
          key: 'issuer',
          name: this.$t('attributes.issuer'),
          valid:
            this.payment?.payment_mode === 'LEV' ||
            (this.payment?.tier_payant === 1 &&
              !!this.payment?.payer_type &&
              (!!this.payment?.payer_id || !!this.payment?.issuer_name)) ||
            (this.payment?.tier_payant === 0 &&
              !!this.payment?.issuer_name?.length)
        },
        {
          key: 'amount',
          name: this.$t('invoicing.amount'),
          valid: this.payment?.amount !== null && !this.blockingChequeDuplicate
        }
      ]

      if (this.withLinkingStep) {
        // Nothing is required here, so the step must never hold the user back:
        // leaving every box as it came is a valid answer.
        steps.push({
          key: 'linking',
          name: this.$t('global.payment.link_documents_title'),
          valid: true
        })
      }

      if (this.withOtherSection) {
        steps.push({
          key: 'other',
          name: this.$t('attributes.other_data'),
          valid: false
        })
      }

      return steps
    },
    /**
     * Only a cheque is held back by its number: once the mode changes, the
     * field is gone and a stale duplicate must not block a step it no longer
     * shows.
     */
    blockingChequeDuplicate() {
      return !!this.chequeDuplicate && isChequeMode(this.payment?.payment_mode)
    },
    stepValid() {
      return this.stepsForProgressBar[this.step]?.valid === true
    },
    allIsValid() {
      return this.stepsList
        .filter((step) => step.key !== 'other' && step.key !== 'linking')
        .every((step) => step.valid === true)
    },
    selectedPaymentMode() {
      return this.payment_modes.find(
        (payment_mode) => this.payment.payment_mode === payment_mode.id
      )
    },
    /**
     * Returns the default bank account id of the funeral home (agency) related to the invoice or dossier if it exists
     * @return {string|null}
     */
    defaultBankAccountId() {
      const funeralHomeId = this.funeralHomeId ?? this.invoice?.funeral_home_id
      return (
        this.initialMetadata.models?.funeralHomes?.find(
          (funeralHome) => funeralHome.id === funeralHomeId
        )?.default_bank_account_id ?? null
      )
    }
  },
  watch: {
    step() {
      this.$nextTick(this.updateHasContentBelow)
    },
    /**
     * The candidates are fetched while the modal is already open. Inserting the
     * step under a user who is past it would swap the content of the step being
     * read, so the index follows the insertion.
     */
    withLinkingStep(isShown) {
      if (this.step >= LINKING_STEP_INDEX) {
        this.localStep = this.step + (isShown ? 1 : -1)
      }
      this.$nextTick(this.updateHasContentBelow)
    }
  },
  mounted() {
    this.updateHasContentBelow()
    if (typeof ResizeObserver === 'undefined' || !this.$refs.stepArea) {
      return
    }
    // The step content grows as fields appear, the area shrinks with the
    // window: neither fires a scroll event.
    this.stepAreaObserver = new ResizeObserver(this.updateHasContentBelow)
    this.stepAreaObserver.observe(this.$refs.stepArea)
    this.stepAreaObserver.observe(this.$refs.stepContent)
  },
  beforeUnmount() {
    this.stepAreaObserver?.disconnect()
  },
  methods: {
    /**
     * macOS hides overlay scrollbars until the user scrolls, so the options at
     * the bottom of the last step gave no hint they were there.
     */
    updateHasContentBelow() {
      const stepArea = this.$refs.stepArea
      this.hasContentBelow =
        !!stepArea &&
        stepArea.scrollTop + stepArea.clientHeight < stepArea.scrollHeight - 1
    }
  }
})
</script>
